// =============================================================================
// SHANIZ GAMING ADMIN - PAYOUTS
// =============================================================================

import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Skeleton,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Replay as RetryIcon,
  CheckCircle as ApproveIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { payoutService, Payout, PayoutStatus, userService } from '../services/api';
import { PermissionButton } from '../components/PermissionButton';

const STATUS_TABS: { key: PayoutStatus; label: string; color: 'warning' | 'info' | 'success' | 'error' }[] = [
  { key: 'PENDING', label: 'Pending', color: 'warning' },
  { key: 'PROCESSING', label: 'Processing', color: 'info' },
  { key: 'COMPLETED', label: 'Completed', color: 'success' },
  { key: 'FAILED', label: 'Failed', color: 'error' },
];

const StatusChip: React.FC<{ status: PayoutStatus }> = ({ status }) => {
  const map: Record<PayoutStatus, 'warning' | 'info' | 'success' | 'error' | 'default'> = {
    PENDING: 'warning',
    PROCESSING: 'info',
    COMPLETED: 'success',
    FAILED: 'error',
    CANCELLED: 'default',
  };
  return <Chip size="small" color={map[status]} label={status} />;
};

const Payouts: React.FC = () => {
  const qc = useQueryClient();
  const [tab, setTab] = useState<PayoutStatus>('PENDING');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');

  const [selected, setSelected] = useState<Payout | null>(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [manualNote, setManualNote] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' }>({ open: false, msg: '', sev: 'success' });

  // Create-manual-payout dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [userQuery, setUserQuery] = useState('');
  const [userDebounced, setUserDebounced] = useState('');
  const [selectedUser, setSelectedUser] = useState<{ id: string; username?: string; email?: string; phoneNumber?: string } | null>(null);
  const [newPayout, setNewPayout] = useState<{
    amount: string;
    method: 'MPESA' | 'CARD' | 'BANK';
    phoneNumber: string;
    cardLast4: string;
    bankName: string;
    bankAccount: string;
    reason: string;
    reference: string;
  }>({
    amount: '',
    method: 'MPESA',
    phoneNumber: '',
    cardLast4: '',
    bankName: '',
    bankAccount: '',
    reason: '',
    reference: '',
  });

  React.useEffect(() => {
    const t = setTimeout(() => setUserDebounced(userQuery), 300);
    return () => clearTimeout(t);
  }, [userQuery]);

  const userSearch = useQuery({
    queryKey: ['payouts', 'user-search', userDebounced],
    queryFn: () => userService.search(userDebounced, 8),
    enabled: userDebounced.trim().length >= 2,
  });

  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const list = useQuery({
    queryKey: ['payouts', tab, page + 1, rowsPerPage, debounced],
    queryFn: () => payoutService.getAll({ page: page + 1, limit: rowsPerPage, status: tab, search: debounced || undefined }),
  });

  const items = useMemo<Payout[]>(() => {
    const d: any = list.data;
    const raw = d?.items ?? d?.data ?? d?.payouts ?? (Array.isArray(d) ? d : []);
    return (Array.isArray(raw) ? raw : []) as Payout[];
  }, [list.data]);
  const total = list.data?.pagination?.total ?? list.data?.total ?? items.length;

  const detail = useQuery({
    queryKey: ['payouts', 'detail', selected?.id],
    queryFn: () => payoutService.getById(selected!.id),
    enabled: Boolean(selected?.id),
  });

  const retry = useMutation({
    mutationFn: (id: string) => payoutService.retry(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payouts'] });
      setSnackbar({ open: true, msg: 'Retry queued', sev: 'success' });
    },
    onError: (e: Error) => setSnackbar({ open: true, msg: e.message || 'Retry failed', sev: 'error' }),
  });

  const manual = useMutation({
    mutationFn: ({ id, code, note }: { id: string; code: string; note?: string }) =>
      payoutService.manualApprove(id, code, note),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payouts'] });
      setManualOpen(false);
      setManualCode('');
      setManualNote('');
      setSnackbar({ open: true, msg: 'Payout approved manually', sev: 'success' });
    },
    onError: (e: Error) => setSnackbar({ open: true, msg: e.message || 'Approve failed', sev: 'error' }),
  });

  const createPayout = useMutation({
    mutationFn: (payload: Parameters<typeof payoutService.create>[0]) => payoutService.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payouts'] });
      setCreateOpen(false);
      setSelectedUser(null);
      setUserQuery('');
      setUserDebounced('');
      setNewPayout({
        amount: '',
        method: 'MPESA',
        phoneNumber: '',
        cardLast4: '',
        bankName: '',
        bankAccount: '',
        reason: '',
        reference: '',
      });
      setSnackbar({ open: true, msg: 'Manual payout queued', sev: 'success' });
    },
    onError: (e: any) =>
      setSnackbar({ open: true, msg: e?.response?.data?.message || e?.message || 'Create failed', sev: 'error' }),
  });

  // Derived validation — the submit button must stay disabled until every
  // required section is filled in for the chosen method.
  const amountNumber = Number(newPayout.amount);
  const methodDestinationValid = (() => {
    if (newPayout.method === 'MPESA') return /^(?:\+?254|0)?\d{9,12}$/.test(newPayout.phoneNumber.replace(/\s+/g, ''));
    if (newPayout.method === 'CARD') return newPayout.cardLast4.length === 4;
    if (newPayout.method === 'BANK') return newPayout.bankName.trim().length > 0 && newPayout.bankAccount.trim().length >= 4;
    return false;
  })();
  const createValid = Boolean(
    selectedUser?.id &&
      amountNumber > 0 &&
      newPayout.reason.trim().length >= 3 &&
      methodDestinationValid,
  );

  const handleCreateSubmit = () => {
    if (!selectedUser?.id) return;
    const phone =
      newPayout.method === 'MPESA'
        ? newPayout.phoneNumber.trim()
        : newPayout.method === 'BANK'
          ? `${newPayout.bankName.trim()} · ${newPayout.bankAccount.trim()}`
          : `**** **** **** ${newPayout.cardLast4}`;
    createPayout.mutate({
      userId: selectedUser.id,
      amount: Math.round(amountNumber),
      method: newPayout.method,
      phoneNumber: phone || undefined,
      reason: newPayout.reason.trim(),
      reference: newPayout.reference.trim() || undefined,
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          Payouts
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            placeholder="Search user / reference"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            sx={{ width: 320 }}
          />
          <Tooltip title="Refresh">
            <IconButton onClick={() => list.refetch()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': { borderColor: 'text.primary', bgcolor: 'action.hover' },
            }}
          >
            Manual payout
          </Button>
        </Box>
      </Box>

      <Card>
        <Tabs
          value={tab}
          onChange={(_, v) => {
            setTab(v);
            setPage(0);
          }}
          sx={{ borderBottom: '1px solid #262626' }}
        >
          {STATUS_TABS.map((t) => (
            <Tab key={t.key} value={t.key} label={t.label} />
          ))}
        </Tabs>
        <CardContent>
          {list.isError && <Alert severity="error">Failed to load payouts.</Alert>}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Destination</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.isLoading &&
                  Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 8 }).map((__, j) => (
                        <TableCell key={j}>
                          <Skeleton variant="text" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                {!list.isLoading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Box sx={{ py: 4, textAlign: 'center' }}>
                        <Typography color="text.secondary">No payouts in {tab} state</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {items.map((p) => (
                  <TableRow key={p.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {p.user?.username || p.userId?.slice(0, 8)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {p.user?.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={600}>
                        {p.currency || 'KES'} {(Number(p.amount) || 0).toLocaleString()}
                      </Typography>
                      {p.fees ? (
                        <Typography variant="caption" color="text.secondary">
                          net {p.currency || 'KES'} {(Number(p.netAmount ?? (Number(p.amount) || 0) - (Number(p.fees) || 0)) || 0).toLocaleString()}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>{p.method}</TableCell>
                    <TableCell>{p.provider}</TableCell>
                    <TableCell>{p.destination}</TableCell>
                    <TableCell>{dayjs(p.createdAt).format('MMM D HH:mm')}</TableCell>
                    <TableCell>
                      <StatusChip status={p.status} />
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="outlined" onClick={() => setSelected(p)}>
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total || 0}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 20, 50, 100]}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </CardContent>
      </Card>

      {/* Detail drawer */}
      <Drawer anchor="right" open={Boolean(selected)} onClose={() => setSelected(null)}
        PaperProps={{ sx: { width: { xs: '100%', md: 520 }, bgcolor: '#121212' } }}>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>
              Payout {selected?.id?.slice(0, 10)}
            </Typography>
            <IconButton onClick={() => setSelected(null)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {detail.isLoading && <CircularProgress />}
          {detail.isError && <Alert severity="error">Failed to load payout detail.</Alert>}
          {detail.data && (
            <Stack spacing={2}>
              <Box sx={{ p: 2, bgcolor: '#1A1A1A', borderRadius: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <StatusChip status={detail.data.status} />
                  <Typography variant="h5" fontWeight={700}>
                    {detail.data.currency || 'KES'} {(Number(detail.data.amount) || 0).toLocaleString()}
                  </Typography>
                </Stack>
                <Divider sx={{ my: 1.5 }} />
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography color="text.secondary" variant="body2">User</Typography>
                    <Typography variant="body2">{detail.data.user?.username}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography color="text.secondary" variant="body2">Method / Provider</Typography>
                    <Typography variant="body2">{detail.data.method} · {detail.data.provider}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography color="text.secondary" variant="body2">Destination</Typography>
                    <Typography variant="body2">{detail.data.destination}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography color="text.secondary" variant="body2">Reference</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {detail.data.reference || '—'}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography color="text.secondary" variant="body2">Created</Typography>
                    <Typography variant="body2">{dayjs(detail.data.createdAt).format('MMM D, YYYY HH:mm')}</Typography>
                  </Stack>
                  {detail.data.processedAt && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography color="text.secondary" variant="body2">Processed</Typography>
                      <Typography variant="body2">{dayjs(detail.data.processedAt).format('MMM D, YYYY HH:mm')}</Typography>
                    </Stack>
                  )}
                  {detail.data.failureReason && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {detail.data.failureReason}
                    </Alert>
                  )}
                </Stack>
              </Box>

              <Box sx={{ p: 2, bgcolor: '#1A1A1A', borderRadius: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Transaction log</Typography>
                {(detail.data.logs || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No log entries yet.</Typography>
                ) : (
                  <Stack spacing={1}>
                    {detail.data.logs!.map((l, i) => (
                      <Box key={i} sx={{ borderLeft: '2px solid', borderColor: l.level === 'error' ? 'error.main' : l.level === 'warn' ? 'warning.main' : 'info.main', pl: 1.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {dayjs(l.at).format('MMM D HH:mm:ss')}
                        </Typography>
                        <Typography variant="body2">{l.event}</Typography>
                        {l.detail && <Typography variant="caption" color="text.secondary">{l.detail}</Typography>}
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>

              <Stack direction="row" spacing={1} justifyContent="flex-end">
                {detail.data.status === 'FAILED' && (
                  <PermissionButton
                    permission="payouts.retry"
                    variant="outlined"
                    startIcon={<RetryIcon />}
                    disabled={retry.isPending}
                    onClick={() => retry.mutate(detail.data!.id)}
                  >
                    Retry
                  </PermissionButton>
                )}
                {(detail.data.status === 'PENDING' || detail.data.status === 'FAILED') && (
                  <PermissionButton
                    permission="payouts.approve"
                    variant="contained"
                    color="success"
                    startIcon={<ApproveIcon />}
                    onClick={() => setManualOpen(true)}
                  >
                    Manual Approve
                  </PermissionButton>
                )}
              </Stack>
            </Stack>
          )}
        </Box>
      </Drawer>

      {/* Manual approve 2FA dialog */}
      <Dialog open={manualOpen} onClose={() => setManualOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Manual Approval</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Enter the 6-digit confirmation code from your authenticator to manually approve this payout.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Confirmation code"
            value={manualCode}
            onChange={(e) => setManualCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputProps={{ maxLength: 6, inputMode: 'numeric' }}
            sx={{ mt: 1 }}
          />
          <TextField
            fullWidth
            label="Note (optional)"
            value={manualNote}
            onChange={(e) => setManualNote(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            disabled={manualCode.length !== 6 || manual.isPending || !selected}
            onClick={() => selected && manual.mutate({ id: selected.id, code: manualCode, note: manualNote || undefined })}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------- Create Manual Payout Dialog ---------- */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" fontWeight={700}>Manual payout</Typography>
          <Typography variant="body2" color="text.secondary">
            Issue a payout on behalf of a user. This will create a pending withdrawal that operations will settle.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3.5} sx={{ mt: 1 }}>
            {/* Section 1 — Recipient */}
            <Box>
              <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing={1}>
                1 · Recipient
              </Typography>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth size="small"
                    label="Search user"
                    placeholder="Start typing username…"
                    value={userQuery}
                    onChange={(e) => setUserQuery(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                  />
                  {userDebounced.trim().length >= 2 && !selectedUser && (
                    <Box sx={{ mt: 1, maxHeight: 220, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                      {userSearch.isLoading && <Box sx={{ p: 1.5 }}><Typography variant="caption" color="text.secondary">Searching…</Typography></Box>}
                      {!userSearch.isLoading && (userSearch.data || []).length === 0 && (
                        <Box sx={{ p: 1.5 }}><Typography variant="caption" color="text.secondary">No matches</Typography></Box>
                      )}
                      {(userSearch.data || []).map((u: any) => (
                        <Box
                          key={u.id}
                          onClick={() => {
                            setSelectedUser({
                              id: u.id,
                              username: u.username,
                              email: u.email,
                              phoneNumber: u.phoneNumber,
                            });
                            setNewPayout((p) => ({ ...p, phoneNumber: u.phoneNumber || p.phoneNumber }));
                          }}
                          sx={{ px: 1.5, py: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, borderBottom: 1, borderColor: 'divider' }}
                        >
                          <Typography variant="body2" fontWeight={600}>{u.username}</Typography>
                          <Typography variant="caption" color="text.secondary">{u.email || u.id}</Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Grid>
                {selectedUser && (
                  <Grid item xs={12}>
                    <Box sx={{ p: 2, borderRadius: 1, bgcolor: 'action.hover', border: 1, borderColor: 'divider' }}>
                      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{selectedUser.username}</Typography>
                          <Typography variant="caption" color="text.secondary">{selectedUser.email || '—'}</Typography>
                          {selectedUser.phoneNumber && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {selectedUser.phoneNumber}
                            </Typography>
                          )}
                        </Box>
                        <Button size="small" onClick={() => { setSelectedUser(null); setUserQuery(''); }}>
                          Change
                        </Button>
                      </Stack>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>

            <Divider />

            {/* Section 2 — Amount & method */}
            <Box>
              <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing={1}>
                2 · Amount & method
              </Typography>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth size="small" type="number"
                    label="Amount"
                    value={newPayout.amount}
                    onChange={(e) => setNewPayout({ ...newPayout, amount: e.target.value })}
                    InputProps={{ startAdornment: <InputAdornment position="start">KES</InputAdornment> }}
                    error={newPayout.amount !== '' && amountNumber <= 0}
                    helperText={newPayout.amount !== '' && amountNumber <= 0 ? 'Must be greater than zero' : ' '}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {[500, 1000, 2000, 5000].map((v) => (
                      <Chip
                        key={v}
                        label={`KES ${v.toLocaleString()}`}
                        variant={String(v) === newPayout.amount ? 'filled' : 'outlined'}
                        onClick={() => setNewPayout({ ...newPayout, amount: String(v) })}
                      />
                    ))}
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <ToggleButtonGroup
                    exclusive size="small" color="primary"
                    value={newPayout.method}
                    onChange={(_, v) => v && setNewPayout({ ...newPayout, method: v })}
                  >
                    <ToggleButton value="MPESA">M-Pesa</ToggleButton>
                    <ToggleButton value="CARD">Card</ToggleButton>
                    <ToggleButton value="BANK">Bank</ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
                {newPayout.method === 'MPESA' && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth size="small"
                      label="Recipient phone"
                      placeholder="e.g. 0712345678"
                      value={newPayout.phoneNumber}
                      onChange={(e) => setNewPayout({ ...newPayout, phoneNumber: e.target.value })}
                      error={newPayout.phoneNumber !== '' && !/^(?:\+?254|0)?\d{9,12}$/.test(newPayout.phoneNumber.replace(/\s+/g, ''))}
                      helperText="Kenyan mobile number; we'll sanitise before sending"
                    />
                  </Grid>
                )}
                {newPayout.method === 'CARD' && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth size="small"
                      label="Card last 4"
                      value={newPayout.cardLast4}
                      onChange={(e) => setNewPayout({ ...newPayout, cardLast4: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                      inputProps={{ maxLength: 4, inputMode: 'numeric' }}
                      helperText="We only store the masked last 4 digits"
                    />
                  </Grid>
                )}
                {newPayout.method === 'BANK' && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth size="small"
                        label="Bank name"
                        value={newPayout.bankName}
                        onChange={(e) => setNewPayout({ ...newPayout, bankName: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth size="small"
                        label="Account number"
                        value={newPayout.bankAccount}
                        onChange={(e) => setNewPayout({ ...newPayout, bankAccount: e.target.value })}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>

            <Divider />

            {/* Section 3 — Reason & reference */}
            <Box>
              <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing={1}>
                3 · Reason & reference
              </Typography>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth size="small" multiline minRows={2}
                    label="Reason"
                    placeholder="Explain why this payout is being issued manually"
                    value={newPayout.reason}
                    onChange={(e) => setNewPayout({ ...newPayout, reason: e.target.value })}
                    required
                    error={newPayout.reason !== '' && newPayout.reason.trim().length < 3}
                    helperText={newPayout.reason !== '' && newPayout.reason.trim().length < 3 ? 'Minimum 3 characters' : ' '}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth size="small"
                    label="External reference (optional)"
                    placeholder="Auto-generated if left blank"
                    value={newPayout.reference}
                    onChange={(e) => setNewPayout({ ...newPayout, reference: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    An audit trail will record the admin that created this payout and the current timestamp.
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Summary card */}
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover', border: 1, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700} letterSpacing={1} sx={{ display: 'block', mb: 1 }}>
                SUMMARY
              </Typography>
              <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                <Box>
                  <Typography variant="caption" color="text.secondary">User</Typography>
                  <Typography fontWeight={700}>{selectedUser?.username || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Amount</Typography>
                  <Typography fontWeight={700}>KES {amountNumber > 0 ? amountNumber.toLocaleString() : '0'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Method</Typography>
                  <Typography fontWeight={700}>{newPayout.method}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Destination</Typography>
                  <Typography fontWeight={700}>
                    {newPayout.method === 'MPESA'
                      ? (newPayout.phoneNumber || '—')
                      : newPayout.method === 'CARD'
                        ? (newPayout.cardLast4 ? `**** ${newPayout.cardLast4}` : '—')
                        : (newPayout.bankName && newPayout.bankAccount ? `${newPayout.bankName} · ${newPayout.bankAccount}` : '—')}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setCreateOpen(false)} sx={{ color: 'text.secondary' }}>Cancel</Button>
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleCreateSubmit}
            disabled={!createValid || createPayout.isPending}
            sx={{
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': { borderColor: 'text.primary', bgcolor: 'action.hover' },
              px: 3,
            }}
          >
            {createPayout.isPending ? 'Creating…' : 'Create manual payout'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.sev} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Payouts;
